 
import {useQuery} from '@tanstack/react-query'
import {useParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {RestrictionRuleControllerRuleViolationsEndpoint} from '@/types/endpoint-contracts'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import {formatTimestamp} from '@/lib/date-utils'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {EntityLink} from '@/components/shared/EntityLink'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {MutedText} from '@/components/shared/MutedText'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {PageLayout} from '@/components/shared/PageLayout'
import {Pagination} from '@/components/ui/pagination'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'

export function RestrictionRuleViolations() {
    const i18n = useI18n()
    const services = useServices()
    const {id} = useParams<{id: string}>()

    const {data: rule, isLoading: ruleLoading} = useQuery({
        queryKey: ['restriction-rule', id],
        queryFn: () => services.restrictionRules.detail(id!),
        enabled: !!id,
    })

    const title = rule
        ? i18n.restrictionRules.violationsPageTitle({name: rule.name})
        : String(i18n.restrictionRules.violations)

    usePageTitle(title as string)

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: RestrictionRuleControllerRuleViolationsEndpoint,
        sortable: true,
        defaultSortBy: RestrictionRuleControllerRuleViolationsEndpoint.sort.DETECTED_AT,
        defaultSortDir: SortDir.DESC,
    })

    const {data: violations, isLoading: violationsLoading, error} = useQuery({
        queryKey: ['restriction-rule-violations', id, queryString, page, pageSize],
        queryFn: () => services.restrictionRules.ruleViolations(id!, Object.fromEntries(new URLSearchParams(queryString))),
        enabled: !!id,
    })

    const isLoading = ruleLoading || violationsLoading

    const filters = (
        <UnifiedSearchBar
            contract={RestrictionRuleControllerRuleViolationsEndpoint}
            value={searchAndFilters}
            onChange={setSearchAndFilters}
            testIdNamespace="rule-violations"
        />
    )

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.restrictionRuleViolation}
            title={title as string}
            description={i18n.restrictionRules.violationsPageSubtitle}
            backTo={id ? routes.accessGovernance.restrictionRules.detail(id) : routes.accessGovernance.restrictionRules.list()}
            count={violations?.totalElements}
            filters={filters}
            loading={isLoading}
            error={error}
            data-testid="restriction-rule-violations"
            emptyState={
                <InlineEmptyText data-testid="empty-state-violations">
                    {i18n.restrictionRules.noViolationsForRule}
                </InlineEmptyText>
            }
            pagination={violations ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={violations.totalElements}
                    totalPages={violations.totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-identity">
                            {i18n.restrictionRules.identity}
                        </TableHead>
                        <TableHead data-testid="col-violating-profile">
                            {i18n.restrictionRules.violatingProfile}
                        </TableHead>
                        <TableHead data-testid="col-detected-at">
                            {i18n.restrictionRules.detectedAt}
                        </TableHead>
                        <TableHead data-testid="col-resolved-at">
                            {i18n.restrictionRules.resolvedAt}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {violations?.content.map(v => (
                        <ClickableTableRow key={v.id} to={routes.identityManagement.identities.detail(v.identity.id)}>
                            <TableCell>
                                <EntityLink value={v.identity} stopPropagation testId={`link-identity-${v.identity.id}`}/>
                            </TableCell>
                            <TableCell>
                                <EntityLink value={v.violatingProfile} stopPropagation showBadge testId={`link-profile-${v.violatingProfile.id}`}/>
                            </TableCell>
                            <TableCell>
                                <MutedText size="sm">{formatTimestamp(v.detectedAt)}</MutedText>
                            </TableCell>
                            <TableCell>
                                {v.resolvedAt ? (
                                    <Badge variant={BadgeVariant.SUCCESS} data-testid={`badge-resolved-${v.id}`}>
                                        <MutedText size="sm">{formatTimestamp(v.resolvedAt)}</MutedText>
                                    </Badge>
                                ) : (
                                    <Badge variant={BadgeVariant.DESTRUCTIVE} data-testid={`badge-open-${v.id}`}>
                                        {i18n.restrictionRules.openViolations}
                                    </Badge>
                                )}
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
